import { cn } from "@/lib/utils";
import type { Plan } from "@blueskydataplatform/shared-ui/types/plans.types";
import { getPlanMonthlyPrice } from "@blueskydataplatform/shared-ui/utils/get-plan-monthly-price";
import { getPlanPriceInformation } from "@blueskydataplatform/shared-ui/utils/get-plan-price-information";

function getSymbolsCount(plan: Plan): string | null {
  const feature = plan.features?.find(f => f.key === "n_symbols");
  if (!feature) return null;
  const value = (feature.displayValue || feature.value || "").trim();
  if (!value) return null;
  return value.toLowerCase().includes("symbol") ? value : `${value} symbols`;
}

type PlanCardProps = {
  plan: Plan;
  datasetPrice?: number | null;
  isSelected: boolean;
  onSelect: () => void;
};

export function PlanCard({ plan, datasetPrice, isSelected, onSelect }: PlanCardProps) {
  const { priceSubtitle } = getPlanPriceInformation(plan, plan);
  const isFeatured = plan.popular === true;
  const symbolsCount = getSymbolsCount(plan);
  const basePrice = typeof plan.monthlyPrice === "number" ? plan.monthlyPrice : 0;
  const totalPrice = datasetPrice != null ? basePrice + datasetPrice : basePrice;
  const displayPrice = getPlanMonthlyPrice(totalPrice);
  const displaySubtitle = priceSubtitle;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-all",
        isSelected
          ? "border-primary bg-primary/10 dark:bg-primary/20"
          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md"
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-bold text-slate-900 dark:text-white">{plan.name}</span>
          {isFeatured && (
            <span className="rounded-full bg-primary-100 dark:bg-primary-900/50 px-2 py-0.5 text-[10px] font-bold text-primary-700 dark:text-primary-300 uppercase">
              Popular
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          {displayPrice}
          {displaySubtitle && <span className="ml-1">{displaySubtitle}</span>}
          {symbolsCount != null && (
            <>
              <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
              <span className="font-bold">{symbolsCount}</span>
            </>
          )}
        </p>
      </div>
      <span
        className={cn(
          "shrink-0 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors",
          isSelected
            ? "bg-primary text-white"
            : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-primary hover:text-white"
        )}
      >
        {isSelected ? "Selected" : "Select"}
      </span>
    </button>
  );
}
